import { useEffect, useState } from 'react';
import First from 'components/quote/simple/create/First';
import PageTemplate from 'components/template/PageTemplate';
import Second from 'components/quote/simple/create/Second';
import Container from 'components/common/Container';
import { useAtom, useSetAtom } from 'jotai';
import { containerFileAtom, quoteFileAtom, quoteInfoAtom } from 'atom/quote';
import usePreventRefresh from 'hook/usePreventRefresh';
import CustomButton from 'components/common/CustomButton';
import Third from 'components/quote/simple/create/Third';
import Dialog from 'components/common/Dialog';
import { useNavigate } from 'react-router-dom';
import { IContainerList, IPackingList, IQuote } from 'types/quote';
import HsCodeModal from 'components/common/modal/HsCodeModal';
import { hsCodePopAtom, hsCodeResultAtom } from 'atom/hsCode';
import useCheckCompany from 'hook/useCheckCompany';
import { ReactComponent as FileList } from 'assets/common/fileList.svg'
import { addContainerItem, addPackingItem, validateContainerList, validatePackingItem } from 'components/function';

const SimpleCreate = () => {
    const navigate = useNavigate();
    const [quoteInfo, setQuoteInfo] = useAtom(quoteInfoAtom)
    const [popHsCodeOpen, setPoHsCodepOpen] = useAtom(hsCodePopAtom)
    const setHsCodeResult = useSetAtom(hsCodeResultAtom);
    const [quoteFile, setQuoteFile] = useAtom(quoteFileAtom);
    const setContainerFile = useSetAtom(containerFileAtom);
    const [popWeightOpen, setPopWeightOpen] = useState<boolean>(false);
    const [clear, setClear] = useState<boolean>(false);
    const [popDangerListOpen, setPopDangerListOpen] = useState<boolean>(false);
    const [dangerFileArr, setDangerFileArr] = useState<File[]>([]);
    const [containerInfo, setContainerInfo] = useState<IContainerList>({} as IContainerList);
    const [packingInfo, setPackingInfo] = useState<IPackingList>({} as IPackingList);

    usePreventRefresh()
    useCheckCompany();

    useEffect(() => {
        const temp = window.sessionStorage.getItem('simple')
        const type = window.sessionStorage.getItem('type')
        if (temp) {
            const info: IQuote = JSON.parse(temp)
            setQuoteInfo(info)
            if (info.step1.transportType === 'AIR' && type === 'main') {
                setPackingInfo(p => ({
                    ...p,
                    weight: Number(info.amount)
                }))
            }
            setTimeout(() => {
                window.sessionStorage.removeItem('simple')
                window.sessionStorage.removeItem('type')
            }, 2000)
        }
        else {
            setContainerFile([])
            setQuoteFile([])
            setHsCodeResult('')
            setQuoteInfo({} as IQuote)
            setTimeout(() => {
                setQuoteInfo({
                    step1: { tradeType: 'EXPORT', transportType: 'SEA' },
                    step2: { shipmentMode: 'FCL-DRY' }
                } as IQuote);
            }, 200)
        }
    }, [])

    useEffect(() => {
        const {
            step1,
            step2,
            shippingDate,
            isShippingDate
        } = quoteInfo;

        const transportType = step1?.transportType;
        const isSea = transportType === 'SEA';
        const isAir = transportType === 'AIR';

        const hasCommonFields =
            !!step1?.tradeType &&
            !!transportType &&
            !!step1?.originId &&
            !!step1?.destinationId &&
            !!step2?.hsCode &&
            typeof isShippingDate === 'boolean' &&
            (!isShippingDate || !!shippingDate);

        let isValid = false;

        if (isSea) {
            if (!step2?.shipmentMode) {
                setClear(false);
                return;
            }

            const { shipmentMode, containerList, packingList } = step2;

            switch (shipmentMode) {
                case 'FCL-DRY':
                    if (containerInfo.containerType &&
                        containerInfo.containerWeight &&
                        containerInfo.containerQuantity) {
                        if (containerInfo.require === 'flexi') {
                            if (typeof containerInfo.flexiBag === 'boolean') {
                                isValid = true
                            }
                        }

                        if (containerInfo.require === 'frozen') {
                            if (containerInfo.tempMin && containerInfo.tempMax) {
                                isValid = true
                            }
                        }

                        if (containerInfo.isDangerous) {
                            if (containerInfo.dangerFile && containerInfo.dangerFile.length > 0) {
                                isValid = true
                            }
                            else {
                                isValid = false
                            }
                        }
                        break;
                    }
                    else {
                        isValid = validateContainerList(containerList);
                        break;
                    }
                case 'OOG':
                case 'LCL':
                    if (quoteFile.length > 0) {
                        isValid = true
                        break;
                    }
                    else {
                        if (validatePackingItem(packingInfo)) {
                            isValid = true
                            break;
                        }
                        else {
                            isValid = validatePackingList(packingList);
                            break;
                        }
                    }
                default:
                    isValid = false;
            }
        }
        else if (isAir) {
            if (quoteFile.length > 0) {
                isValid = true
            }
            else {
                if (validatePackingItem(packingInfo)) {
                    isValid = true
                }
                else {
                    isValid = validatePackingList(step2?.packingList);
                }
            }
        }

        setClear(hasCommonFields && isValid);
    }, [quoteInfo, quoteFile, containerInfo, packingInfo]);

    useEffect(() => {
        const packingList = quoteInfo?.step2?.packingList ?? [];
        let cbmSum = 0, weightSum = 0, cwSum = 0;

        packingList.map(arr => {
            if (arr.width && arr.height && arr.length) {
                cbmSum += (arr.width * arr.height * arr.length) / (100 ** 3)
            }
            if (arr.weight && arr.quantity) {
                weightSum += (arr.weight * Number(arr.quantity))
            }
            if (arr.chargeableWeight) {
                cwSum += arr.chargeableWeight
            }
        })
        setQuoteInfo({
            ...quoteInfo,
            cbm: cbmSum,
            totalWeight: weightSum,
            totalCW: cwSum,
        })
    }, [quoteInfo?.step2?.packingList])

    const validatePackingList = (list?: IPackingList[], isGage: boolean = false) =>
        Array.isArray(list) &&
        list.length > 0 &&
        list.every(item =>
            typeof item.length === 'number' &&
            typeof item.width === 'number' &&
            typeof item.height === 'number' &&
            typeof item.weight === 'number' &&
            !!item.packageType &&
            typeof item.quantity === 'number' &&
            typeof item.isStackable === 'boolean' &&
            (isGage ? !!item.gage : true)
        );

    const goConfirm = () => {
        if (containerInfo.containerType &&
            containerInfo.containerWeight &&
            containerInfo.containerQuantity) {
            addContainerItem(
                containerInfo,
                setContainerInfo,
                setContainerFile,
                navigate,
                setQuoteInfo,
                'simple'
            )
        }
        else if (packingInfo.packageType) {
            addPackingItem(
                packingInfo,
                setPackingInfo,
                navigate,
                setQuoteInfo,
                'simple'
            )
        }
        else {
            window.sessionStorage.setItem('simple', JSON.stringify(quoteInfo))
            navigate('/quote/simple/confirm');
        }
    }

    return (
        <PageTemplate background='bg-[#F8F8FA]'>
            <Container className="flex flex-col gap-6 tablet:gap-4 mb-6 pt-21 tablet:pt-8">
                {/* {JSON.stringify(quoteInfo)} */}
                <First quoteInfo={quoteInfo} setQuoteInfo={setQuoteInfo} />
                <Second quoteInfo={quoteInfo} setQuoteInfo={setQuoteInfo} />
                <Third
                    quoteInfo={quoteInfo}
                    setQuoteInfo={setQuoteInfo}
                    setPopWeightOpen={setPopWeightOpen}
                    setPopDangerListOpen={setPopDangerListOpen}
                    setDangerFileArr={setDangerFileArr}
                    containerInfo={containerInfo}
                    setContainerInfo={setContainerInfo}
                    setPackingInfo={setPackingInfo}
                    packingInfo={packingInfo}
                />
                <CustomButton
                    disabled={!clear}
                    className='bg-primary mx-auto w-[200px]'
                    title='견적받기'
                    onClick={goConfirm}
                />
            </Container>
            <Dialog
                isOpen={popDangerListOpen}
                setIsOpen={setPopDangerListOpen}
                btnText='닫기'
            >
                {
                    dangerFileArr.map((item, idx) => (
                        <div
                            className='relative flex items-center gap-3 mb-3 px-6 border border-primary rounded h-10 text-primary text-sm'
                            key={idx}>
                            <FileList />
                            {item.name}
                        </div>
                    ))
                }
            </Dialog>
            <Dialog
                isOpen={popWeightOpen}
                setIsOpen={setPopWeightOpen}
                btnText='확인'
            >
                <p className='text-center'>
                    <span className='font-semibold'>운임산출중량 (Chargeable Weight)</span>는 실제중량(Actual Weight)과<br />
                    용적중량(Volume Weight) 둘 중 큰 쪽으로 산정됩니다.
                </p>
            </Dialog>

            <HsCodeModal
                isOpen={popHsCodeOpen}
                setIsOpen={setPoHsCodepOpen}
            />
        </PageTemplate>
    )
}

export default SimpleCreate